import React from 'react';
import { observer } from 'mobx-react';
import ErrorBoundary from 'components/hoc/error_boundary.js';
import styled from '@emotion/styled';
import { gaps } from 'components/layout/stack';
import Button from 'components/button/button';
import { AppUI, ExerciseUI } from '@seedlang/state';
import { isPresent } from '@seedlang/utils';
import { Theme } from '@seedlang/constants';
import { computed } from 'mobx';
import Link from 'components/link';
import eventParamsHelper from 'utils/event_params_helper';
import DownloadLinkTextFileModal, { downloadLinkTitle, isTextFileLink } from 'components/download_link/download_link_text_file_modal';

const Wrapper = styled.div`
  padding: ${gaps.md};
  background: white;
  border-radius: ${props => props.borderRadius};
`;

const AdminLink = styled.div`
  margin-top: ${gaps.xs};
  font-size: 11px;
  text-decoration: underline;
  text-align: center;
  padding: ${gaps.xs};
`;

const Table = styled.table`
  width: 100%;
  min-height: 0.01%;
  overflow-x: auto;

  @media screen and (max-width: 767px) {
    width: 100%;
    margin-bottom: calc(${gaps.sm} + ${gaps.xs});
    overflow-y: hidden;
    -ms-overflow-style: -ms-autohiding-scrollbar;
    border: 1px solid #ddd;
  }

  td {
    padding: ${gaps.xs};
    vertical-align: middle;
  }

  tbody tr:hover {
    background-color: #f5f5f5;
  }

  tr.clickable {
    cursor: pointer;
    &:hover {
      background-color: rgba(0, 0, 0, 0.05);
    }
  }

  ${({ $striped }) =>
    $striped &&
    `
    tbody tr:nth-of-type(odd) {
      background-color: #f9f9f9;
    }
  `}
`;

const RSSLink = styled.span`
  display: flex;
  font-size: 14px;
  align-items: center;
  margin: 0 ${gaps.sm};
  color: #333333;
  cursor: pointer;
  span {
    text-decoration: underline;
  }
  i {
    margin-right: ${gaps.xs};
  }
  span:hover {
    text-decoration: none;
  }
  justify-content: ${props => props.justifyContent};
`;

@observer
class DownloadLinkIndex extends React.Component {

  @computed get worksheet() {
    if (isPresent(this.props.worksheet)) {
      return this.props.worksheet;
    } else if (ExerciseUI.hasWorksheet) {
      return ExerciseUI.worksheet;
    }
    return null;
  }

  openHref = (href) => {
    const link = document.createElement('a');
    link.href = href;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  handleDownloadLinkClick = (buttonConfig, downloadLink, clickTarget, event) => {
    if (event) {
      event.preventDefault();
    }

    this.trackDownloadButtonClick(buttonConfig, downloadLink, clickTarget);

    if (isTextFileLink(buttonConfig.href)) {
      this.textFileModal.open(downloadLink);
    } else {
      this.openHref(buttonConfig.href);
    }
  }

  blockedUntilMigrated = (downloadLink) => {
    return downloadLink.blockIfNotMigrated && AppUI.shouldBlockPerksForMigration;
  }

  getButtonConfig(downloadLink) {
    const isBlocked = downloadLink.blocked;
    const needsToCreateAccount = isBlocked && ExerciseUI.worksheet.accessWithoutMembership;
    const needsToMigrate = this.blockedUntilMigrated(downloadLink) && !isBlocked;
    const needsToBecomeMember = !ExerciseUI.user?.isMember && isBlocked && !ExerciseUI.worksheet.accessWithoutMembership;
    const needsToUpgradeMembershipType = ExerciseUI.user?.isMember && isBlocked && !ExerciseUI.worksheet.accessWithoutMembership;

    if (needsToCreateAccount) {
      return { locked: true, to: { name: 'onboarding.register' }, text: 'Create Free Account to Try', reason: 'create_free_account' };
    }
    if (needsToBecomeMember) {
      return { locked: true, to: { name: 'memberships.index' }, text: 'Become a Member to Unlock', reason: 'become_a_member' };
    }
    if (needsToUpgradeMembershipType) {
      return { locked: true, to: { name: 'memberships.index' }, text: 'Upgrade to Unlock', reason: 'upgrade' };
    }
    if (needsToMigrate) {
      return { locked: true, to: { name: 'settings.billing' }, text: 'Migrate Payment', reason: 'migrate' };
    }

    if (isBlocked) return null;

    return {
      href: downloadLink.text,
      text: 'Open',
    };
  }
  trackNoAccessButtonClick = (buttonConfig, downloadLink) => {
    if (!buttonConfig?.locked || AppUI.siteIsSeedlang) return;

    AppUI.createEvent('memrev - click no access button', eventParamsHelper({
      source: 'download_link',
      button_text: buttonConfig.text,
      reason: buttonConfig.reason,
      source_route: AppUI.routeStore.routeName,
      source_path: window.location.pathname,
      destination_route: buttonConfig.to?.name,
      download_link_id: downloadLink.id,
      worksheet_id: this.worksheet?.id,
    }));
  }
  trackDownloadButtonClick = (buttonConfig, downloadLink, clickTarget) => {
    if (buttonConfig?.locked || !buttonConfig?.href || AppUI.siteIsSeedlang) return;

    AppUI.createEvent('memrev - click download button', eventParamsHelper({
      button_text: buttonConfig.text,
      button_type: downloadLink.title,
      click_target: clickTarget,
      source_route: AppUI.routeStore.routeName,
      source_path: window.location.pathname,
      download_link_id: downloadLink.id,
      download_link_type_id: downloadLink.downloadLinkTypeId,
      download_link_title: downloadLink.title,
      download_link_icon: downloadLink.icon,
      has_podcast_feed: isPresent(downloadLink.podcastId),
      worksheet_id: this.worksheet?.id,
    }));
  }
  renderButton(buttonConfig) {
    return (
      <Button
        height='30px'
        width='98%'
        fontSize='14px'
        color='#FFF'
        borderRadius='5px'
        scaleOnHover={AppUI.layout.isMobile ? null : '1.03'}
        backgroundOnActive={Theme.gray}
        iconMarginRight={AppUI.layout.isMobile ? '0px' : '3px'}
        background={buttonConfig.locked ? '#AAA' : (AppUI.site.accentColor || '#716f6f')}
      >
        <i
          className={`fa fa-${buttonConfig.locked ? 'lock' : 'arrow-right'}`}
          style={buttonConfig.locked ? null : { transform: 'rotate(-45deg)' }}
        />
        {AppUI.layout.isMobile ? '' : buttonConfig.text}
      </Button>
    );
  }

  render() {
    return (
      <Wrapper borderRadius={this.props.borderRadius}>
        {
          isPresent(this.worksheet) &&
            <Table $striped={AppUI.layout.isDesktop}>
              <tbody>
                {
                  this.worksheet.downloadLinks.map(item => {
                    const buttonConfig = this.getButtonConfig(item);
                    const isDownloadLink = isPresent(buttonConfig?.href);
                    return (
                      <React.Fragment key={item.id}>
                        <tr
                          className={isDownloadLink ? 'clickable' : ''}
                        >
                          <td width='20px'>
                            <i
                              className={`fa fa-${item.icon || 'link'}`}
                              style={{ margin: `${gaps.xs} ${gaps.sm} ${gaps.xs} ${gaps.md}` }}
                            />
                          </td>
                          <td
                            style={{display: 'flex', alignItems: 'center', minHeight: '40px', justifyContent: 'space-between'}}
                          >
                            <span
                              onClick={isDownloadLink ? (event) => this.handleDownloadLinkClick(buttonConfig, item, 'title', event) : undefined}
                              style={{flex: 1}}
                            >
                              {downloadLinkTitle(item)}
                            </span>
                            {
                              !item.blocked && isPresent(item.podcastId) && !AppUI.layout.isMobile &&
                                <Link
                                  to={{name: 'settings.audio_feeds', params: {podcastId: item.podcastId}}}
                                  target='_blank'
                                >
                                  <RSSLink>
                                    <i className='fa fa-rss' /><span>Also available as a podcast feed</span>
                                  </RSSLink>
                                </Link>
                            }
                          </td>
                          <td width={AppUI.layout.isMobile ? '50px' : '280px'}>
                            {
                              buttonConfig && isDownloadLink && (
                                <a
                                  href={buttonConfig.href}
                                  target='_blank'
                                  rel='noopener noreferrer'
                                  onClick={(event) => this.handleDownloadLinkClick(buttonConfig, item, 'button', event)}
                                >
                                  {this.renderButton(buttonConfig)}
                                </a>
                              )
                            }
                            {
                              buttonConfig && !isDownloadLink && (
                                <Link
                                  to={buttonConfig.to}
                                  onClick={() => this.trackNoAccessButtonClick(buttonConfig, item)}
                                >
                                  {this.renderButton(buttonConfig)}
                                </Link>
                              )
                            }
                          </td>
                        </tr>
                        {
                          !item.blocked && isPresent(item.podcastId) && AppUI.layout.isMobile &&
                            <tr>
                              <td colSpan='3'>
                                <Link
                                  to={{name: 'settings.audio_feeds', params: {podcastId: item.podcastId}}}
                                  target='_blank'
                                >
                                  <RSSLink justifyContent='flex-end'>
                                    <i className='fa fa-rss' /><span>Also available as a podcast feed</span>
                                  </RSSLink>
                                </Link>
                              </td>
                            </tr>
                        }
                      </React.Fragment>
                    );
                  })
                }
              </tbody>
            </Table>
        }
        {
          ExerciseUI.user.anyAdmin && this.worksheet &&
            <AdminLink>
              <Link
                to={{name: 'creator.worksheets.edit', params: { worksheetId: this.worksheet.id}}}
              >
                Admin Link
              </Link>
            </AdminLink>
        }
        <DownloadLinkTextFileModal
          ref={(component) => this.textFileModal = component}
        />
      </Wrapper>
    );
  }
}

export default ErrorBoundary(DownloadLinkIndex);
